import { useLoaderData } from 'react-router';
import { PhoneNumberType } from '../../../common/enums.js';
import { formatTimestampToDDMMYYYY, getNextAllowedDay } from '../../../common/utils/dateUtils.js';
import { localizePortabilityError } from '../../../common/utils/phoneNumberUtils.js';
import { ValidatePortableResponse } from '../../../generated/api/validatePortableResponse.js';
import { SimTypeCoCo } from '../placeOrderTypesCoCo.js';
import { ProductOfferConfigField, getFieldsToShow } from '../utils/characteristicsFlagsUtilsCoCo.js';
import { useNumberPublicityCoCo } from './useNumberPublicityCoCo';
import { useSubscriptionConfigFormBase } from './useSubscriptionConfigFormBase.js';
import type { UseSubscriptionConfigFormResult } from './useSubscriptionConfigFormBase.js';
import type { PlaceProductOrderLoaderData } from '../../../common/loaders.js';
import type { ProductOfferingCartItems } from '../../../common/types/shoppingBasketCoCo.js';
import type { ProductOfferingsResponse } from '../../../generated/api/productOfferingsResponse.js';
import type { SubscriptionConfigFormState } from '../utils/subscriptionConfigurationUtilsCoCo.js';

export type { InternalFormData, UseSubscriptionConfigFormResult } from './useSubscriptionConfigFormBase.js';

export interface UseSubscriptionConfigFormOptions {
  cartItemInstanceId: string;
  basketItem: ProductOfferingCartItems;
  mdmId: string;
  phoneNumbers: string[];
  selectedProductOffers: ProductOfferingsResponse[];
}

export const useSubscriptionConfigFormCoCo = ({
  cartItemInstanceId,
  basketItem,
  mdmId,
  phoneNumbers,
  selectedProductOffers,
}: UseSubscriptionConfigFormOptions): UseSubscriptionConfigFormResult => {
  const { holidays } = useLoaderData() as PlaceProductOrderLoaderData;

  const fieldsList = getFieldsToShow({ selectedProductOffers, basketItemId: basketItem.id });

  const cartItemInstanceIdParts = cartItemInstanceId.split('-');
  const basketItemIndex =
    cartItemInstanceIdParts.length > 1
      ? Number.parseInt(cartItemInstanceIdParts[cartItemInstanceIdParts.length - 1], 10)
      : 0;
  useNumberPublicityCoCo(basketItem.cartItemId, basketItemIndex);

  const getDefaultFormStateFallback = (): SubscriptionConfigFormState => ({
    phoneNumberType: PhoneNumberType.NEW,
    transferDate: formatTimestampToDDMMYYYY(getNextAllowedDay(holidays).getTime()),
    differentDonor: false,
    fixedTermContract: false,
    numberPublicity: true,
    simType: SimTypeCoCo.PHYSICAL,
  });

  return useSubscriptionConfigFormBase({
    cartItemInstanceId,
    basketItemId: basketItem.cartItemId,
    mdmId,
    phoneNumbers,
    holidays,
    fieldsToShow: {
      showSelectPhoneNumber: fieldsList.includes(ProductOfferConfigField.NEW_NUMBER),
      showNumberPrivacy: fieldsList.includes(ProductOfferConfigField.NUMBER_PRIVACY),
      showSimCard:
        fieldsList.includes(ProductOfferConfigField.ESIM) || fieldsList.includes(ProductOfferConfigField.PSIM),
      showM2MSimCard: fieldsList.includes(ProductOfferConfigField.ACTIVE_SIM),
    },
    getDefaultFormStateFallback,
    localizePhoneError: (result: ValidatePortableResponse) => {
      if (
        !result.portable &&
        result.errorType &&
        result.errorType !== ValidatePortableResponse.ErrorTypeEnum.TECHNICAL_ERROR
      ) {
        return localizePortabilityError(result.errorType);
      }
      return undefined;
    },
    isNewPhoneRequired: () => true,
  });
};
